import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ErrorBoundary from './ErrorBoundary';
import reportWebVitals from './reportWebVitals';

// CRITICAL: Suppress AbortError BEFORE React loads
// This must run before any React code to prevent the error overlay
(function() {
  // Store original methods
  const originalError = console.error;
  const originalWarn = console.warn;
  
  // Override console.error to filter AbortError
  console.error = function(...args) {
    const errorString = args.map(arg => {
      if (arg instanceof Error) return arg.message + ' ' + (arg.stack || '');
      if (typeof arg === 'object') {
        try { return JSON.stringify(arg); } catch { return String(arg); }
      }
      return String(arg);
    }).join(' ');
    
    // Suppress AbortError and related errors
    if (
      errorString.includes('AbortError') || 
      errorString.includes('signal is aborted') ||
      errorString.includes('The user aborted a request') ||
      errorString.includes('The operation was aborted')
    ) {
      // Silently ignore - this is expected behavior
      return;
    }
    
    // Pass through all other errors
    originalError.apply(console, args);
  };
  
  // Override console.warn to filter AbortError warnings
  console.warn = function(...args) {
    const warnString = String(args[0] || '');
    if (warnString.includes('AbortError') || warnString.includes('aborted')) {
      return;
    }
    originalWarn.apply(console, args);
  };
  
  // Handle unhandled promise rejections BEFORE React
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const isAbortError = 
      reason?.name === 'AbortError' || 
      reason?.message?.includes('signal is aborted') ||
      reason?.message?.includes('aborted') ||
      reason?.message?.includes('The operation was aborted') ||
      reason?.silent === true;
      
    if (isAbortError) {
      // Prevent the error from propagating
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }, true); // Use capture phase
  
  // Handle window errors BEFORE React
  window.addEventListener('error', (event) => {
    const message = event.message || event.error?.message || '';
    if (
      message.includes('AbortError') || 
      message.includes('signal is aborted') ||
      message.includes('The operation was aborted') ||
      event.error?.name === 'AbortError'
    ) {
      // Prevent the error from propagating
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }, true); // Use capture phase
})();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
